// Ridge logistic regression by IRLS: the one fitter the models share.
//
// It came out of `closeLine.js`, which needed a logistic fit whose RESPONSE IS A
// PROBABILITY rather than a 0/1 label (the Bernoulli score equations are
// X'(y − mu) either way, so a fractional response is an ordinary
// quasi-likelihood and the solver does not care). `cricketToss.js` needs the
// same fitter on an ordinary 0/1 label, and two copies of an IRLS loop in one
// engine is two things to get subtly different.
//
// It is deliberately small and knows nothing about betting: rows in, weights
// out, with an intercept prepended and the ridge on everything but it. What a
// feature MEANS is the model's business; this file only refuses to invert a
// singular design.
//
// The same arithmetic runs in `encompassing.js`, which fits the market-versus-
// model blend. That one stays where it is: it carries its own standard errors
// and its own t, which is the whole point of it, and a shared fitter that also
// had to answer those would be a worse fitter for both.

const MAX_ITER = 50;
const TOL = 1e-10;
const P_CLIP = 1e-6;
const ETA_CLIP = 35;
const MIN_WEIGHT = 1e-10;

export const clampEta = (eta) => Math.min(Math.max(eta, -ETA_CLIP), ETA_CLIP);
export const sigmoid = (eta) => 1 / (1 + Math.exp(-eta));
export const logit = (p) => {
  const c = Math.min(Math.max(p, P_CLIP), 1 - P_CLIP);
  return Math.log(c / (1 - c));
};

/** Solve a symmetric positive-definite system by Gauss-Jordan with partial pivoting. */
export function solve(a, b) {
  const k = b.length;
  const m = a.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < k; col++) {
    let pivot = col;
    for (let r = col + 1; r < k; r++) {
      if (Math.abs(m[r][col]) > Math.abs(m[pivot][col])) pivot = r;
    }
    if (!(Math.abs(m[pivot][col]) > 1e-12)) return null;
    [m[col], m[pivot]] = [m[pivot], m[col]];
    const d = m[col][col];
    for (let j = col; j <= k; j++) m[col][j] /= d;
    for (let r = 0; r < k; r++) {
      if (r === col) continue;
      const f = m[r][col];
      if (f === 0) continue;
      for (let j = col; j <= k; j++) m[r][j] -= f * m[col][j];
    }
  }
  const out = m.map((row) => row[k]);
  return out.every(Number.isFinite) ? out : null;
}

/**
 * IRLS on `[{ x, y }]` with an intercept prepended and a ridge on everything but
 * it. `y` may be a label or a probability. Null where the design is singular.
 */
export function fitRidge(rows, width, lambda) {
  const k = width + 1;
  let beta = new Array(k).fill(0);
  for (let iter = 0; iter < MAX_ITER; iter++) {
    const gradient = new Array(k).fill(0);
    const hessian = Array.from({ length: k }, () => new Array(k).fill(0));
    for (const { x, y } of rows) {
      let eta = beta[0];
      for (let i = 0; i < width; i++) eta += beta[i + 1] * x[i];
      const mu = sigmoid(clampEta(eta));
      const w = Math.max(mu * (1 - mu), MIN_WEIGHT);
      const residual = y - mu;
      const full = [1, ...x];
      for (let i = 0; i < k; i++) {
        gradient[i] += residual * full[i];
        for (let j = 0; j < k; j++) hessian[i][j] += w * full[i] * full[j];
      }
    }
    for (let i = 1; i < k; i++) {
      gradient[i] -= lambda * beta[i];
      hessian[i][i] += lambda;
    }
    const step = solve(hessian, gradient);
    if (!step) return null;
    beta = beta.map((b, i) => b + step[i]);
    if (!beta.every(Number.isFinite)) return null;
    if (step.every((d) => Math.abs(d) < TOL)) break;
  }
  return beta;
}

/** Column means and standard deviations, with a zero-variance column left alone. */
export function standardise(rows, width) {
  const means = new Array(width).fill(0);
  const sds = new Array(width).fill(1);
  if (!rows.length) return { means, sds };
  for (const { x } of rows) {
    for (let i = 0; i < width; i++) means[i] += x[i];
  }
  for (let i = 0; i < width; i++) means[i] /= rows.length;
  // The sums of squares start at ZERO and the 1s go back in afterwards. Started
  // from the fill they would leave a constant column with a standard deviation
  // of 1/sqrt(n) instead of 1 — sixty times too small on a season, which turns
  // a column carrying no information into the loudest one in the design.
  const ss = new Array(width).fill(0);
  for (const { x } of rows) {
    for (let i = 0; i < width; i++) ss[i] += (x[i] - means[i]) ** 2;
  }
  for (let i = 0; i < width; i++) {
    const variance = ss[i] / Math.max(1, rows.length - 1);
    sds[i] = variance > 1e-12 ? Math.sqrt(variance) : 1;
  }
  return { means, sds };
}

/**
 * A fitted row's probability, or null. `weights[0]` is the intercept and the
 * rest line up with `x` after the same standardising the fit used.
 */
export function predictRidge(weights, x, means = [], sds = []) {
  if (!Array.isArray(weights) || weights.length !== x.length + 1) return null;
  let eta = weights[0];
  for (let i = 0; i < x.length; i++) {
    const sd = Number.isFinite(sds[i]) && sds[i] > 0 ? sds[i] : 1;
    eta += weights[i + 1] * ((x[i] - (means[i] ?? 0)) / sd);
  }
  const p = sigmoid(clampEta(eta));
  return Number.isFinite(p) && p > 0 && p < 1 ? p : null;
}
